// Money is always whole cents (integers). Tax rates are parts-per-million
// (10.75% = 107_500 ppm, 8.625% = 86_250 ppm) so every rate is exact.

/** a * b / d, rounded half away from zero, using integer math. */
export function mulDivRound(a: number, b: number, d: number): number {
  if (d === 0) throw new Error('division by zero');
  const negative = (a < 0) !== (b < 0) !== (d < 0);
  const n = Math.abs(a) * Math.abs(b);
  const ad = Math.abs(d);
  const q = Math.floor(n / ad);
  const r = n - q * ad;
  const v = r * 2 >= ad ? q + 1 : q;
  return negative && v !== 0 ? -v : v;
}

export function taxOn(taxableCents: number, ratePpm: number): number {
  return mulDivRound(taxableCents, ratePpm, 1_000_000);
}

/**
 * Split `total` into integer parts proportional to `weights` (largest-remainder),
 * so the parts always add up exactly to `total`.
 */
export function allocate(total: number, weights: number[]): number[] {
  const sumW = weights.reduce((s, w) => s + Math.max(0, w), 0);
  if (weights.length === 0) return [];
  if (sumW === 0 || total === 0) return weights.map(() => 0);
  const raw = weights.map((w) => (Math.max(0, w) * total) / sumW);
  const parts = raw.map((x) => Math.floor(x));
  let rest = total - parts.reduce((s, p) => s + p, 0);
  const order = raw
    .map((x, i) => ({ i, frac: x - Math.floor(x), w: weights[i] }))
    .sort((a, b) => b.frac - a.frac || b.w - a.w || a.i - b.i);
  for (let k = 0; rest > 0 && k < order.length; k++, rest--) parts[order[k].i] += 1;
  return parts;
}

export interface CalcLineInput {
  qty: number;
  unitPriceCents: number;
  discountCents?: number;
  taxable: boolean;
}

export interface CalcLine {
  gross: number;
  lineDiscount: number;
  cartDiscountShare: number;
  net: number;
  tax: number;
}

export interface CalcResult {
  lines: CalcLine[];
  subtotal: number;
  discount: number;
  taxable: number;
  tax: number;
  total: number;
}

/**
 * Cart math used by both the screen and the server (the server's result is final).
 * Line discounts come off their line; the cart discount is spread over lines in
 * proportion to their value; tax is charged on the taxable lines after discounts.
 */
export function calculateCart(lines: CalcLineInput[], cartDiscountCents: number, ratePpm: number): CalcResult {
  const gross = lines.map((l) => l.qty * l.unitPriceCents);
  const lineDiscount = lines.map((l, i) => clamp(l.discountCents ?? 0, 0, gross[i]));
  const afterLine = gross.map((g, i) => g - lineDiscount[i]);
  const afterLineSum = afterLine.reduce((s, x) => s + x, 0);
  const cartDiscount = clamp(cartDiscountCents, 0, afterLineSum);
  const shares = allocate(cartDiscount, afterLine);
  const net = afterLine.map((a, i) => a - shares[i]);
  const taxableNet = net.map((n, i) => (lines[i].taxable ? n : 0));
  const taxableTotal = taxableNet.reduce((s, x) => s + x, 0);
  const tax = taxOn(taxableTotal, ratePpm);
  const taxParts = allocate(tax, taxableNet);
  const out: CalcLine[] = lines.map((_, i) => ({
    gross: gross[i],
    lineDiscount: lineDiscount[i],
    cartDiscountShare: shares[i],
    net: net[i],
    tax: taxParts[i],
  }));
  const subtotal = gross.reduce((s, x) => s + x, 0);
  const discount = lineDiscount.reduce((s, x) => s + x, 0) + cartDiscount;
  const netSum = net.reduce((s, x) => s + x, 0);
  return { lines: out, subtotal, discount, taxable: taxableTotal, tax, total: netSum + tax };
}

/** Portion of a line being refunded: exact remainder on the last units, proportional otherwise. */
export function refundPortion(
  line: { qty: number; net: number; tax: number; refundedQty: number; refundedNet: number; refundedTax: number },
  qty: number,
): { net: number; tax: number } {
  const remainingQty = line.qty - line.refundedQty;
  if (qty <= 0 || qty > remainingQty) throw new Error('invalid refund quantity');
  if (qty === remainingQty) {
    return { net: line.net - line.refundedNet, tax: line.tax - line.refundedTax };
  }
  return { net: mulDivRound(line.net, qty, line.qty), tax: mulDivRound(line.tax, qty, line.qty) };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(Math.max(n, lo), hi);
}

export function formatCents(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const dollars = Math.floor(abs / 100);
  const c = String(abs % 100).padStart(2, '0');
  const d = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '−' : ''}$${d}.${c}`;
}

/** "12", "12.5", "$1,234.56" -> cents. Returns null when it isn't a money amount. */
export function parseMoney(input: string): number | null {
  const s = String(input ?? '').replace(/[$,\s]/g, '');
  if (!/^-?(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(s)) return null;
  const negative = s.startsWith('-');
  const [whole, frac = ''] = s.replace('-', '').split('.');
  const cents = Number(whole || '0') * 100 + Number((frac + '00').slice(0, 2));
  return negative ? -cents : cents;
}

export function formatRatePpm(ppm: number): string {
  const pct = (ppm / 10_000).toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return `${pct}%`;
}

export function parseRatePercent(input: string): number | null {
  const s = String(input ?? '').replace(/[%\s]/g, '');
  if (!/^\d+(\.\d{1,4})?$/.test(s)) return null;
  const [w, f = ''] = s.split('.');
  return Number(w) * 10_000 + Number((f + '0000').slice(0, 4));
}
